import type { Abbrev, Category, Phrase, QuickFire } from '../../core/types';
import { normaliseSeed } from './swiss';

/**
 * Day-one vocabulary. Seeded from German AAC Kernvokabular rather than raw corpus
 * frequency, because a board built from the ~200–300 highest-frequency core words
 * is useful in the first conversation, and a board built from news-corpus
 * frequency is not (§5.3).
 *
 * Everything here is authored in Swiss orthography and passed through
 * `normaliseSeed` anyway, so a `ß` typed by accident cannot reach the database.
 * The printed no-tech kit (1.16) is generated from these same arrays, so the paper
 * board and the screen can never drift apart.
 */

/**
 * Quick fires — fixed set, fixed order, on every surface, exempt from learning.
 * These carry more conversational weight per hit than any sentence: burying them in
 * a category, or letting usage reorder them, would be a serious error (§4.1).
 */
const QUICK_FIRES_RAW: QuickFire[] = [
  { id: 'qf-ja', text: 'Ja', order: 0 },
  { id: 'qf-nein', text: 'Nein', order: 1 },
  { id: 'qf-warte', text: 'Warte', order: 2 },
  { id: 'qf-stopp', text: 'Stopp', order: 3 },
  { id: 'qf-aua', text: 'Aua', order: 4 },
  { id: 'qf-danke', text: 'Danke', order: 5 },
];

const CATEGORIES_RAW: Category[] = [
  { id: 'cat-notfall', name: 'Notfall', colour: '#a30000', icon: '⚠️', order: 0, locked: true },
  { id: 'cat-grundbedarf', name: 'Grundbedürfnisse', colour: '#0043ce', icon: '🙋', order: 1 },
  { id: 'cat-essen', name: 'Essen und Trinken', colour: '#8a3800', icon: '🍽️', order: 2 },
  { id: 'cat-koerper', name: 'Körper und Pflege', colour: '#0f62fe', icon: '🛁', order: 3 },
  { id: 'cat-gefuehle', name: 'Gefühle', colour: '#198038', icon: '💚', order: 4 },
  { id: 'cat-gespraech', name: 'Gespräch', colour: '#6929c4', icon: '💬', order: 5 },
  { id: 'cat-alltag', name: 'Alltag und Zeit', colour: '#4589ff', icon: '🕰️', order: 6 },
];

/** `order` is per category. `locked` phrases cannot be edited away (1.10). */
const PHRASE_SEED: ReadonlyArray<{
  categoryId: string;
  text: string;
  shortLabel: string;
  locked?: boolean;
}> = [
  // Notfall — the protected core set. Highest-consequence screen in the app.
  { categoryId: 'cat-notfall', text: 'Hilfe!', shortLabel: 'Hilfe', locked: true },
  {
    categoryId: 'cat-notfall',
    text: 'Ich muss auf die Toilette.',
    shortLabel: 'Toilette',
    locked: true,
  },
  { categoryId: 'cat-notfall', text: 'Ich habe Schmerzen.', shortLabel: 'Schmerzen', locked: true },
  { categoryId: 'cat-notfall', text: 'Ich habe Durst.', shortLabel: 'Durst', locked: true },
  { categoryId: 'cat-notfall', text: 'Bitte warten.', shortLabel: 'Warten', locked: true },

  // Grundbedürfnisse
  { categoryId: 'cat-grundbedarf', text: 'Ich möchte aufstehen.', shortLabel: 'Aufstehen' },
  { categoryId: 'cat-grundbedarf', text: 'Ich möchte mich hinlegen.', shortLabel: 'Hinlegen' },
  { categoryId: 'cat-grundbedarf', text: 'Bitte hilf mir kurz.', shortLabel: 'Hilf mir' },
  { categoryId: 'cat-grundbedarf', text: 'Mir ist zu warm.', shortLabel: 'Zu warm' },
  { categoryId: 'cat-grundbedarf', text: 'Mir ist zu kalt.', shortLabel: 'Zu kalt' },
  { categoryId: 'cat-grundbedarf', text: 'Bitte mach das Licht an.', shortLabel: 'Licht an' },
  { categoryId: 'cat-grundbedarf', text: 'Bitte mach das Licht aus.', shortLabel: 'Licht aus' },

  // Essen und Trinken
  {
    categoryId: 'cat-essen',
    text: 'Ich hätte gern ein Glas Wasser.',
    shortLabel: 'Wasser',
  },
  { categoryId: 'cat-essen', text: 'Nicht zu kalt, bitte.', shortLabel: 'Nicht kalt' },
  { categoryId: 'cat-essen', text: 'Ich habe Hunger.', shortLabel: 'Hunger' },
  { categoryId: 'cat-essen', text: 'Ich möchte einen Kaffee.', shortLabel: 'Kaffee' },
  { categoryId: 'cat-essen', text: 'Das war gut, danke.', shortLabel: 'War gut' },
  { categoryId: 'cat-essen', text: 'Ich möchte nichts mehr essen.', shortLabel: 'Genug' },

  // Körper und Pflege
  { categoryId: 'cat-koerper', text: 'Ich möchte mich waschen.', shortLabel: 'Waschen' },
  { categoryId: 'cat-koerper', text: 'Bitte die Zähne putzen.', shortLabel: 'Zähne' },
  { categoryId: 'cat-koerper', text: 'Ich möchte andere Kleider.', shortLabel: 'Kleider' },
  { categoryId: 'cat-koerper', text: 'Bitte das Kissen richten.', shortLabel: 'Kissen' },
  { categoryId: 'cat-koerper', text: 'Ich sitze unbequem.', shortLabel: 'Unbequem' },
  { categoryId: 'cat-koerper', text: 'Bitte die Brille putzen.', shortLabel: 'Brille' },

  // Gefühle
  { categoryId: 'cat-gefuehle', text: 'Es geht mir heute besser.', shortLabel: 'Besser' },
  { categoryId: 'cat-gefuehle', text: 'Ich bin müde.', shortLabel: 'Müde' },
  { categoryId: 'cat-gefuehle', text: 'Ich habe schlecht geschlafen.', shortLabel: 'Schlecht geschlafen' },
  {
    categoryId: 'cat-gefuehle',
    text: 'Ich freue mich, dich zu sehen.',
    shortLabel: 'Freue mich',
  },
  { categoryId: 'cat-gefuehle', text: 'Ich bin traurig.', shortLabel: 'Traurig' },
  { categoryId: 'cat-gefuehle', text: 'Lass mich kurz allein, bitte.', shortLabel: 'Allein' },

  // Gespräch
  { categoryId: 'cat-gespraech', text: 'Grüezi!', shortLabel: 'Grüezi' },
  { categoryId: 'cat-gespraech', text: 'Bitte langsamer sprechen.', shortLabel: 'Langsamer' },
  { categoryId: 'cat-gespraech', text: 'Bitte warte, ich schreibe.', shortLabel: 'Ich schreibe' },
  {
    categoryId: 'cat-gespraech',
    text: 'Das habe ich nicht verstanden.',
    shortLabel: 'Nicht verstanden',
  },
  { categoryId: 'cat-gespraech', text: 'Bitte wiederhol das.', shortLabel: 'Wiederholen' },
  { categoryId: 'cat-gespraech', text: 'Ja, genau.', shortLabel: 'Genau' },
  { categoryId: 'cat-gespraech', text: 'Nein, etwas anderes.', shortLabel: 'Anderes' },
  { categoryId: 'cat-gespraech', text: 'Auf Wiedersehen.', shortLabel: 'Wiedersehen' },

  // Alltag und Zeit
  { categoryId: 'cat-alltag', text: 'Wie spät ist es?', shortLabel: 'Wie spät' },
  { categoryId: 'cat-alltag', text: 'Wann kommt der Besuch?', shortLabel: 'Besuch' },
  { categoryId: 'cat-alltag', text: 'Ich möchte nach draussen.', shortLabel: 'Draussen' },
  { categoryId: 'cat-alltag', text: 'Bitte das Fenster öffnen.', shortLabel: 'Fenster' },
  { categoryId: 'cat-alltag', text: 'Ich möchte Musik hören.', shortLabel: 'Musik' },
  { categoryId: 'cat-alltag', text: 'Bitte den Fernseher leiser.', shortLabel: 'Leiser' },
];

/**
 * Starter abbreviations. Deterministic, motor-memory friendly, and — unlike
 * prediction — they impose no visual search cost at all, which for an unknown
 * visual impairment may make them the better rate enhancement (§5.3b). The
 * caregiver edits this list; it is a starting point, not a fixed vocabulary.
 */
const ABBREVS_RAW: Abbrev[] = [
  { code: 'ffd', expansion: 'Ich freue mich, dich zu sehen.' },
  { code: 'bhm', expansion: 'Bitte hilf mir kurz.' },
  { code: 'gwa', expansion: 'Ich hätte gern ein Glas Wasser.' },
  { code: 'mgb', expansion: 'Es geht mir heute besser.' },
  { code: 'wc', expansion: 'Ich muss auf die Toilette.' },
  { code: 'bws', expansion: 'Bitte warte, ich schreibe.' },
];

/**
 * Core vocabulary for the printed board (1.16). Roughly the set that covers ~80 %
 * of everyday utterances; kept as data so the paper board is generated, never
 * transcribed by hand.
 */
const KERNVOKABULAR_RAW: string[] = [
  'ich', 'du', 'wir', 'nicht', 'ja', 'nein',
  'hier', 'da', 'weg', 'noch', 'mal', 'mehr',
  'auch', 'und', 'was', 'wer', 'wo', 'wann',
  'wie', 'warum', 'möchte', 'will', 'kann', 'haben',
  'gehen', 'kommen', 'essen', 'trinken', 'sehen', 'hören',
  'gut', 'schlecht', 'gross', 'klein', 'jetzt', 'später',
  'bitte', 'danke', 'fertig', 'aua',
];

export const SEED_QUICK_FIRES: readonly QuickFire[] = normaliseSeed(QUICK_FIRES_RAW);
export const SEED_CATEGORIES: readonly Category[] = normaliseSeed(CATEGORIES_RAW);
export const SEED_ABBREVS: readonly Abbrev[] = normaliseSeed(ABBREVS_RAW);
export const SEED_KERNVOKABULAR: readonly string[] = normaliseSeed(KERNVOKABULAR_RAW);

export const SEED_PHRASES: readonly Phrase[] = normaliseSeed(
  PHRASE_SEED.map((seed, index): Phrase => {
    const orderInCategory = PHRASE_SEED.filter(
      (other, i) => other.categoryId === seed.categoryId && i < index,
    ).length;
    return {
      id: `ph-${seed.categoryId.replace(/^cat-/, '')}-${orderInCategory}`,
      categoryId: seed.categoryId,
      text: seed.text,
      shortLabel: seed.shortLabel,
      order: orderInCategory,
      pinned: false,
      useCount: 0,
      ...(seed.locked ? { locked: true } : {}),
    };
  }),
);
